import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import "./loaded-trucks.css";

import { selectUser } from "../../features/userSlice";
import FloatingActions from "../../components/FloatingActions";
import { usePopup } from "../../utils/PopupContext";



/* -------------------------DATE FORMAT ------------------------------- */
const formatDateTime = (value) => {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};


/* ---------------------------NUMBER--------------------------------- */
const numberValue = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : 0;
};


/* -------------------------SLIP STATUS------------------------------- */
const getStatus = (slip) => {
    if (slip?.p_outdate || slip?.P_SECONDWT) {
        return {
            label: "Completed",
            className: "completed",
        };
    }

    // if (slip?.p_loading_end) {
    //   return {
    //     label: "Completed",
    //     className: "Completed",
    //   };
    // }

    // if (slip?.p_loading_start) {
    //   return {
    //     label: "Loading",
    //     className: "loading",
    //   };
    // }

    return {
        label: "Pending",
        className: "pending",
    };
};


const MyLoadedTrucks = () => {
    const user = useSelector(selectUser);
    const navigate = useNavigate();
    const { showPopup } = usePopup();

    const topRef = useRef(null);
    const bottomRef = useRef(null);

    const today = new Date();
    const tomorrow = new Date(today);

    tomorrow.setDate(tomorrow.getDate() + 1);

    const formatInputDate = (date) => {
        return date.toISOString().split("T")[0];
    };

    const [inDate, setInDate] = useState(formatInputDate(today));
    const [outDate, setOutDate] = useState(formatInputDate(tomorrow));

    const [loading, setLoading] = useState(false);
    const [excelFileLoading, setExcelFileLoading] = useState(false);
    const [loadedTrucksList, setLoadedTrucksList] = useState([]);

    const [expandedIndex, setExpandedIndex] = useState(null);
    const [activeTab, setActiveTab] = useState({});

    /* ----------------------------GROUP API RESPONSE-------------------------------- */
    const groupByLoadingPoint = (data = []) => {
        const grouped = new Map();

        data.forEach((parent) => {
            /*
             * IMPORTANT:
             * Every object inside advice is a CHILD.
             *
             * We never use:
             * parent.advice?.[0]
             */
            (parent.advice || []).forEach((child) => {

                const pointKey =
                    `${child.LOADING_POINT_CODE}__${child.LOADING_POINT_NAME}`;


                /* ---------------- Loading Point ---------------- */

                if (!grouped.has(pointKey)) {
                    grouped.set(pointKey, {
                        loadingPointCode: child.LOADING_POINT_CODE,
                        loadingPointName: child.LOADING_POINT_NAME,

                        /*
                         * Map is temporarily used so the same document
                         * can contain multiple child/advice records.
                         */
                        slips: new Map(),
                    });
                }


                const point = grouped.get(pointKey);


                /* ---------------- Slip ---------------- */

                if (!point.slips.has(parent.P_DOCNUMBER)) {
                    point.slips.set(parent.P_DOCNUMBER, {
                        P_DOCNUMBER: parent.P_DOCNUMBER,
                        P_entity_code: parent.P_entity_code,

                        /*
                         * Party name belongs to slip object.
                         */
                        P_Party_name: parent.P_Party_name,
                        P_Party_Code: parent.P_Party_Code,

                        p_loading_start: parent.p_loading_start,
                        p_loading_end: parent.p_loading_end,

                        p_load_unload_oper_Start:
                            parent.p_load_unload_oper_Start,

                        p_load_unload_oper_End:
                            parent.p_load_unload_oper_End,

                        P_LoadUnloadRemark:
                            parent.P_LoadUnloadRemark,

                        p_outdate: parent.p_outdate,

                        P_SECONDWT: parent.P_SECONDWT,

                        p_before_url: parent.p_before_url,
                        p_after_url: parent.p_after_url,

                        /*
                         * ALL children for this document
                         * go inside this array.
                         */
                        items: [],
                    });
                }


                const slip = point.slips.get(parent.P_DOCNUMBER);


                /* ---------------- Child Item ---------------- */

                slip.items.push({
                    P_TCODE: child.P_TCODE,
                    ADVICE_DATE: child.ADVICE_DATE,

                    VEHICLE_NO: child.VEHICLE_NO,

                    LOADING_POINT_CODE:
                        child.LOADING_POINT_CODE,

                    LOADING_POINT_NAME:
                        child.LOADING_POINT_NAME,

                    SLNO: child.SLNO,

                    ITEM_CODE: child.ITEM_CODE,
                    ITEM_DESC: child.ITEM_DESC,

                    QTY_REQUIRED:
                        child.QTY_REQUIRED,

                    QTY_LOADED:
                        child.QTY_LOADED,
                });
            });
        });


        /*
         * Convert Maps into normal arrays.
         */
        return Array.from(grouped.values()).map((point) => ({
            ...point,
            slips: Array.from(point.slips.values()),
        }));
    };

    const handleExportExcel = async () => {
        setExcelFileLoading(true);
        try {
            const payload = {
                inDate,
                outDate,
            };

            const authCfg = {
                headers: {
                    Authorization: `Bearer ${user?.token}`,
                },
                responseType: "blob",
            };

            const resp = await axios.post(
                "/api/loading/exportLoadedTrucks",
                payload,
                authCfg
            );

            const blob = new Blob([resp.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");

            link.href = url;
            link.download = `loaded-trucks-${inDate || "all"}.xlsx`;

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            showPopup({
                variant: "error",
                title: "Something Went Wrong",
                message: error.response?.data?.message || "Unable to load truck information. Please try again."
            });
        }
        finally {
            setExcelFileLoading(false);
        }
    };

    /* ----------------------------FETCH DATA------------------------------- */
    const getLoadedTrucks = async () => {
        try {
            setLoading(true);

            const payload = {
                inDate,
                outDate,
                page: null,
                limit: null
            };

            const authCfg = {
                headers: {
                    Authorization: `Bearer ${user?.token}`,
                },
            };

            const resp = await axios.post(
                "/api/loading/getLoadedTrucks",
                payload,
                authCfg
            );

            const data = Array.isArray(resp.data.data)
                ? resp.data.data
                : [];

            const groupedData = groupByLoadingPoint(data);

            setLoadedTrucksList(groupedData);

            setExpandedIndex(null);

            /*
             * Reset tabs after new API data.
             */
            setActiveTab({});

        } catch (error) {
            showPopup({
                variant: "error",
                title: "Something Went Wrong",
                message: error.response?.data?.message || "Unable to load truck information. Please try again."
            });
        } finally {
            setLoading(false);
        }
    };


    // useEffect(() => {
    //     getLoadedTrucks();
    // }, []);


    /* -------------------------------------------------------------
     * LOADING POINT STATUS
     * ------------------------------------------------------------- */

    const getGroupStatus = (slips = []) => {
        if (!slips.length) {
            return {
                label: "No Data",
                className: "pending",
            };
        }

        const completedCount = slips.filter(
            (slip) => slip?.p_outdate
        ).length;

        if (completedCount === slips.length) {
            return {
                label: "Completed",
                className: "completed",
            };
        }

        const loadingCount = slips.filter(
            (slip) =>
                slip?.p_loading_start &&
                !slip?.p_loading_end
        ).length;

        if (loadingCount > 0) {
            return {
                label: "Loading",
                className: "loading",
            };
        }

        return {
            label: "Loaded",
            className: "loaded",
        };
    };


    /* ---------------------------UNIQUE TRUCK COUNT----------------------------- */
    const getUniqueTruckCount = (slips = []) => {
        const trucks = new Set();

        slips.forEach((slip) => {
            (slip.items || []).forEach((item) => {
                if (item.VEHICLE_NO) {
                    trucks.add(item.VEHICLE_NO);
                }
            });
        });

        return trucks.size;
    };


    /* ---------------------------TOTAL LOADED QTY-------------------------------- */
    const getTotalQty = (slips = []) => {
        return slips.reduce(
            (total, slip) => {
                return (
                    total +
                    (slip.items || []).reduce(
                        (sum, item) =>
                            sum + numberValue(item.QTY_LOADED),
                        0
                    )
                );
            },
            0
        );
    };


    /* -------------------------TOTAL REQUIRED QTY------------------------------ */
    const getRequiredQty = (slips = []) => {
        return slips.reduce(
            (total, slip) => {
                return (
                    total +
                    (slip.items || []).reduce(
                        (sum, item) =>
                            sum + numberValue(item.QTY_REQUIRED),
                        0
                    )
                );
            },
            0
        );
    };


    /* -------------------------------------------------------------
     * PARTY TAB DATA
     *
     * IMPORTANT:
     * Process ALL objects inside slips.
     * ------------------------------------------------------------- */

    const getPartyData = (slips = []) => {
        const partyMap = new Map();

        slips.forEach((slip) => {
            const party = slip.P_Party_name || "-";

            if (!partyMap.has(party)) {
                partyMap.set(party, {
                    party,
                    trucks: new Set(),
                    loaded_qty: 0,
                    req_qty: 0
                });
            }


            const row = partyMap.get(party);


            (slip.items || []).forEach((item) => {
                if (item.VEHICLE_NO) {
                    row.trucks.add(item.VEHICLE_NO);
                }

                row.loaded_qty += numberValue(item.QTY_LOADED);
                row.req_qty += numberValue(item.QTY_REQUIRED);
            });
        });


        return Array.from(
            partyMap.values()
        );
    };


    /* -------------------------------------------------------------
     * TRUCK TAB DATA
     *
     * ALL slips -> ALL items
     * ------------------------------------------------------------- */

    const getTruckData = (slips = []) => {
        const truckMap = new Map();


        slips.forEach((slip) => {

            (slip.items || []).forEach((item) => {

                const vehicle =
                    item.VEHICLE_NO || "-";


                if (!truckMap.has(vehicle)) {
                    truckMap.set(vehicle, {
                        vehicle,
                        party:
                            slip.P_Party_name || "-",
                        qty: 0,
                        required: 0,
                    });
                }


                const row =
                    truckMap.get(vehicle);


                row.qty += numberValue(
                    item.QTY_LOADED
                );

                row.required += numberValue(
                    item.QTY_REQUIRED
                );
            });
        });


        return Array.from(
            truckMap.values()
        );
    };


    /* -------------------------------------------------------------
     * ITEM TAB DATA
     *
     * ALL slips -> ALL items
     * ------------------------------------------------------------- */

    const getItemData = (slips = []) => {
        return slips.flatMap((slip) =>
            (slip.items || []).map((item, index) => ({
                ...item,
                party: slip.P_Party_name || "-",
                uniqueKey: `${slip.P_DOCNUMBER}-${item.SLNO || index}`,
            }))
        );
    };


    /* -------------------------------------------------------------
     * TAB CHANGE
     * ------------------------------------------------------------- */

    const changeTab = (index, tab) => {
        setActiveTab((prev) => ({
            ...prev,
            [index]: tab,
        }));
    };


    /* -------------------------------------------------------------
     * CURRENT TAB DATA
     * ------------------------------------------------------------- */

    const renderTabContent = (point, index) => {
        const tab = activeTab[index] || "party";
        const slips = point.slips || [];

        /* ============================PARTY============================= */
        if (tab === "party") {
            const rows = getPartyData(slips);

            return (
                <div className="mlt-tab-content">
                    <div className="mlt-compact-table mlt-desktop-party-table">

                        <div className="mlt-compact-header">
                            <div>Supplier</div>
                            <div>Trucks</div>
                            <div>Loaded Qty</div>
                            <div>Required Qty</div>
                        </div>

                        {rows.length === 0 ? (
                            <div className="mlt-no-tab-data">
                                No party data available
                            </div>
                        ) : (
                            rows.map((row) => (
                                <div
                                    className="mlt-compact-row"
                                    key={row.party}
                                >
                                    <div className="mlt-party-name">
                                        <div>{row.party}</div>
                                    </div>

                                    <div>{row.trucks.size}</div>

                                    <div>{row.loaded_qty.toFixed(2)}</div>

                                    <div>{row.req_qty.toFixed(2)}</div>
                                </div>
                            ))
                        )}

                    </div>


                    {/* Mobile UI */}
                    <div className="mlt-mobile-party-list">

                        {rows.length === 0 ? (
                            <div className="mlt-no-tab-data">
                                No party data available
                            </div>
                        ) : (
                            rows.map((row) => (
                                <div
                                    className="mlt-mobile-party-card"
                                    key={row.party}
                                >
                                    <div className="mlt-mobile-party-header">
                                        <div className="mlt-mobile-party-name">
                                            {row.party}
                                        </div>

                                        <div className="mlt-mobile-party-trucks">
                                            {row.trucks.size} Trucks
                                        </div>
                                    </div>

                                    <div className="mlt-mobile-party-details">

                                        <div className="mlt-mobile-party-stat">
                                            <span>Loaded Qty</span>
                                            <strong>
                                                {row.loaded_qty.toFixed(2)}
                                            </strong>
                                        </div>

                                        <div className="mlt-mobile-party-stat">
                                            <span>Required Qty</span>
                                            <strong>
                                                {row.req_qty.toFixed(2)}
                                            </strong>
                                        </div>

                                    </div>
                                </div>
                            ))
                        )}

                    </div>

                </div>
            );
        }


        /* ==========================================================
           TRUCKS
           ========================================================== */

        if (tab === "trucks") {

            const rows =
                getTruckData(slips);


            return (
                <div className="mlt-tab-content">

                    {/* <div className="mlt-compact-table">

                        <div className="mlt-compact-header mlt-truck-grid">
                            <div>Vehicle</div>
                            <div>Party</div>
                            <div>Required</div>
                            <div>Loaded</div>
                        </div>


                        {rows.length === 0 ? (

                            <div className="mlt-no-tab-data">
                                No truck data available
                            </div>

                        ) : (

                            rows.map((row) => (

                                <div
                                    className="mlt-compact-row mlt-truck-grid"
                                    key={row.vehicle}
                                >

                                    <div>
                                        <strong>
                                            {row.vehicle}
                                        </strong>
                                    </div>

                                    <div className="mlt-muted">
                                        {row.party}
                                    </div>

                                    <div>
                                        {row.required.toFixed(2)}
                                    </div>

                                    <div className="mlt-loaded-qty">
                                        {row.qty.toFixed(2)}
                                    </div>

                                </div>
                            ))
                        )}

                    </div> */}
                    {/* Desktop */}
<div className="mlt-compact-table mlt-desktop-truck-table">

    <div className="mlt-compact-header mlt-truck-grid">
        <div>Vehicle</div>
        <div>Party</div>
        <div>Required</div>
        <div>Loaded</div>
    </div>

    {rows.length === 0 ? (

        <div className="mlt-no-tab-data">
            No truck data available
        </div>

    ) : (

        rows.map((row) => (

            <div
                className="mlt-compact-row mlt-truck-grid"
                key={row.vehicle}
            >

                <div>
                    <strong>{row.vehicle}</strong>
                </div>

                <div className="mlt-muted">
                    {row.party}
                </div>

                <div>
                    {row.required.toFixed(2)}
                </div>

                <div className="mlt-loaded-qty">
                    {row.qty.toFixed(2)}
                </div>

            </div>
        ))
    )}

</div>


{/* Mobile */}
<div className="mlt-mobile-truck-list">

    {rows.length === 0 ? (

        <div className="mlt-no-tab-data">
            No truck data available
        </div>

    ) : (

        rows.map((row) => (

            <div
                className="mlt-mobile-truck-card"
                key={row.vehicle}
            >

                <div className="mlt-mobile-truck-header">

                    <div className="mlt-mobile-truck-vehicle">
                        {row.vehicle}
                    </div>

                    <div className="mlt-mobile-truck-party">
                        {row.party}
                    </div>

                </div>


                <div className="mlt-mobile-truck-details">

                    <div className="mlt-mobile-truck-stat">
                        <span>Required</span>
                        <strong>
                            {row.required.toFixed(2)}
                        </strong>
                    </div>

                    <div className="mlt-mobile-truck-stat">
                        <span>Loaded</span>
                        <strong className="mlt-mobile-loaded">
                            {row.qty.toFixed(2)}
                        </strong>
                    </div>

                </div>

            </div>

        ))
    )}

</div>

                </div>
            );
        }


        /* ==========================================================
           ITEMS
           ========================================================== */

        if (tab === "items") {
            const rows = getItemData(slips);

            return (
                <div className="mlt-tab-content">

                    <div className="mlt-compact-table">

                        <div className="mlt-compact-header mlt-item-grid">
                            <div>Item</div>
                            <div>Required</div>
                            <div>Loaded</div>
                        </div>

                        {rows.length === 0 ? (

                            <div className="mlt-no-tab-data">
                                No item data available
                            </div>

                        ) : (

                            rows.map((row) => (

                                <div
                                    className="mlt-compact-row mlt-item-grid"
                                    key={row.uniqueKey}
                                >

                                    <div className="mlt-item-cell">

                                        <strong>
                                            {row.ITEM_CODE || "-"}
                                        </strong>

                                        <span>
                                            {row.ITEM_DESC || "-"}
                                        </span>

                                    </div>

                                    <div>
                                        {numberValue(row.QTY_REQUIRED).toFixed(2)}
                                    </div>

                                    <div className="mlt-loaded-qty">
                                        {numberValue(row.QTY_LOADED).toFixed(2)}
                                    </div>

                                </div>

                            ))
                        )}

                    </div>

                </div>
            );
        }


        return null;
    };


    /* -------------------------------------------------------------
     * CLEAR FILTER
     * ------------------------------------------------------------- */

    const clearFilters = () => {
        setInDate("");
        setOutDate("");
    };


    /* -------------------------------------------------------------
     * REFRESH
     * ------------------------------------------------------------- */

    const refreshData = () => {
        getLoadedTrucks();
    };


    /* -------------------------------------------------------------
     * RENDER
     * ------------------------------------------------------------- */

    return (
        <div className="my-loaded-trucks">
            <div ref={topRef} />
            {/* ======================================================== FILTER ======================================================== */}
            <div className="mlt-filter-bar">
                <div className="mlt-date-field">
                    <input
                        type="date"
                        value={inDate}
                        onChange={(e) =>
                            setInDate(e.target.value)
                        }
                    />
                </div>
                <div className="mlt-date-field">
                    <input
                        type="date"
                        value={outDate}
                        onChange={(e) =>
                            setOutDate(e.target.value)
                        }
                    />
                </div>
                <button
                    className="mlt-clear-btn"
                    onClick={() => setOutDate("")}
                >
                    Clear End Date
                </button>
                <button
                    className="mlt-search-btn"
                    onClick={getLoadedTrucks}
                    disabled={loading}
                >
                    {loading
                        ? "Loading..."
                        : "Search"}
                </button>
                <button
                    className="btn btn-success"
                    onClick={handleExportExcel}
                    disabled={excelFileLoading}
                >
                    {excelFileLoading
                        ? "Preparing..."
                        : "Download XLS"}
                </button>
            </div>

            {/* ========================================================
          MAIN CONTAINER
          ======================================================== */}

            <div className="mlt-container">
                {loading ? (
                    <div className="mlt-loading">
                        <div className="mlt-spinner" />
                        <span>
                            Loading trucks...
                        </span>
                    </div>
                ) : loadedTrucksList.length === 0 ? (
                    <div className="mlt-empty">
                        <div className="mlt-empty-icon">🚛</div>
                        <h3>No loaded trucks found</h3>

                        <p>
                            No loading point data is available
                            for the selected dates.
                        </p>
                    </div>

                ) : (

                    loadedTrucksList.map(
                        (point, index) => {
                            const slips =
                                point.slips || [];

                            const isExpanded =
                                expandedIndex === index;

                            const status =
                                getGroupStatus(slips);

                            const totalTrucks =
                                getUniqueTruckCount(slips);

                            const totalQty =
                                getTotalQty(slips);

                            const totalRequired =
                                getRequiredQty(slips);


                            return (
                                <div
                                    className={`mlt-card-wrapper ${isExpanded
                                        ? "mlt-card-expanded"
                                        : ""
                                        }`}
                                    key={`${point.loadingPointCode}-${index}`}
                                >

                                    <div
                                        className={`mlt-card ${isExpanded
                                            ? "selected"
                                            : ""
                                            }`}
                                    >

                                        {/* ==================================================
                        LOADING POINT HEADER
                        ================================================== */}

                                        <div className="mlt-card-header">
                                            <div className="mlt-card-title" onClick={() =>
                                                setExpandedIndex(
                                                    isExpanded
                                                        ? null
                                                        : index
                                                )
                                            }>
                                                <div className="mlt-loading-icon">🚚</div>
                                                <h3>{point.loadingPointName || "Loading Point"}</h3>
                                                {/* {isExpanded && (
                                                    <div className="mlt-expanded-header">
                                                        <h4>Loading Point Details</h4>
                                                    </div>
                                                )} */}
                                            </div>
                                        </div>


                                        {/* ================================================== CARD SUMMARY ================================================== */}
                                        <div className="mlt-parent-grid">
                                            <div>
                                                <span>Code</span>
                                                <strong>{point.loadingPointCode || "-"}</strong>
                                            </div>
                                            <div>
                                                <span>Slips</span>
                                                <strong>{slips.length}</strong>
                                            </div>
                                            <div>
                                                <span>Trucks</span>
                                                <strong>{totalTrucks}</strong>
                                            </div>
                                            <div>
                                                <span>Loaded Qty</span>
                                                <strong>{totalQty}</strong>
                                            </div>
                                            <div>
                                                <span>Required Qty</span>
                                                <strong>{totalRequired}</strong>
                                            </div>
                                        </div>

                                        {/* ================================================= EXPANDED CONTEN ================================================== */}
                                        {isExpanded && (
                                            <div className="mlt-expanded-content">

                                                {/* ==============================================
                            TABS
                            
                            IMPORTANT:
                            These tabs use ALL point.slips.
                            ============================================== */}

                                                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                                    <div className="mlt-slip-tabs">

                                                        <button
                                                            className={
                                                                (activeTab[index] ||
                                                                    "party") ===
                                                                    "party"
                                                                    ? "active"
                                                                    : ""
                                                            }
                                                            onClick={() =>
                                                                changeTab(
                                                                    index,
                                                                    "party"
                                                                )
                                                            }
                                                        >
                                                            Party
                                                        </button>


                                                        <button
                                                            className={
                                                                activeTab[index] ===
                                                                    "trucks"
                                                                    ? "active"
                                                                    : ""
                                                            }
                                                            onClick={() =>
                                                                changeTab(
                                                                    index,
                                                                    "trucks"
                                                                )
                                                            }
                                                        >
                                                            Trucks
                                                        </button>


                                                        <button
                                                            className={
                                                                activeTab[index] ===
                                                                    "items"
                                                                    ? "active"
                                                                    : ""
                                                            }
                                                            onClick={() =>
                                                                changeTab(
                                                                    index,
                                                                    "items"
                                                                )
                                                            }
                                                        >
                                                            Items
                                                        </button>

                                                    </div>
                                                </div>


                                                {/* ==============================================
                            TAB CONTENT
                            ============================================== */}

                                                {renderTabContent(point, index)}

                                                {/* ==============================================
                            SLIP LIST
                            
                            Each slip is still available below the
                            aggregate tabs.
                            ============================================== */}

                                                {/* <div className="mlt-slip-list">
                                                    <SlipComponent slips={slips} />
                                                </div> */}
                                            </div>
                                        )}


                                        {/* ==================================================
                        VIEW / HIDE BUTTON
                        ================================================== */}

                                        <button
                                            className="mlt-view-btn"
                                            onClick={() =>
                                                setExpandedIndex(
                                                    isExpanded
                                                        ? null
                                                        : index
                                                )
                                            }
                                        >
                                            {isExpanded
                                                ? "Hide Details"
                                                : "View Details"}

                                            <span>
                                                {isExpanded
                                                    ? "▲"
                                                    : "▼"}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            );
                        }
                    )
                )}

            </div>


            <div
                ref={bottomRef}
                className="mlt-bottom-ref"
            />


            <FloatingActions
                topRef={topRef}
                bottomRef={bottomRef}
            />

        </div>
    );
};

const SlipComponent = ({ slips }) => {
    const [show, setShow] = useState(false);

    return (
        <>
            <div className="mlt-slip-list-title">
                <span>
                    Loading Details - {slips.length}
                </span>

                <button className="show-hide-btn" onClick={() => setShow(!show)}>
                    {show ? "Hide Details" : "View Details"}
                </button>
            </div>
            {show && (
                <>
                    {slips.map(
                        (slip) => {

                            const slipStatus =
                                getStatus(slip);

                            const items =
                                slip.items || [];

                            const truckCount =
                                new Set(
                                    items
                                        .map(
                                            (item) =>
                                                item.VEHICLE_NO
                                        )
                                        .filter(Boolean)
                                ).size;

                            const loadedQty =
                                items.reduce(
                                    (
                                        total,
                                        item
                                    ) =>
                                        total +
                                        numberValue(
                                            item.QTY_LOADED
                                        ),
                                    0
                                );


                            const tcodes = [
                                ...new Set(
                                    items
                                        .map(
                                            (item) =>
                                                item.P_TCODE
                                        )
                                        .filter(Boolean)
                                ),
                            ].join(", ");


                            return (
                                <div
                                    className="mlt-slip-card"
                                    key={
                                        slip.P_DOCNUMBER
                                    }
                                >

                                    {/* ==================================
                                      SLIP HEADER
                                      ================================== */}

                                    <div className="mlt-slip-header">

                                        <div>

                                            <div className="mlt-slip-number">
                                                {slip.P_DOCNUMBER ||
                                                    "-"}
                                            </div>

                                            <div className="mlt-slip-entity">
                                                Entity:{" "}
                                                {slip.P_entity_code ||
                                                    "-"}
                                            </div>

                                        </div>


                                        <div
                                            className={`mlt-status ${slipStatus.className}`}
                                        >
                                            {
                                                slipStatus.label
                                            }
                                        </div>

                                    </div>


                                    {/* ==================================
                                      SLIP MINI INFO
                                      ================================== */}

                                    <div className="mlt-slip-mini-info">

                                        <div>
                                            <span>
                                                Party
                                            </span>

                                            <strong>
                                                {
                                                    slip.P_Party_name ||
                                                    "-"
                                                }
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Loading Start
                                            </span>

                                            <strong>
                                                {formatDateTime(
                                                    slip.p_loading_start
                                                )}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Trucks
                                            </span>

                                            <strong>
                                                {truckCount}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Loaded Qty
                                            </span>

                                            <strong>
                                                {loadedQty.toFixed(
                                                    2
                                                )}
                                            </strong>
                                        </div>

                                    </div>


                                    {/* ==================================
                                      SLIP FOOTER
                                      ================================== */}

                                    <div className="mlt-slip-footer">

                                        <div>
                                            <span>
                                                Loading End
                                            </span>

                                            <strong>
                                                {formatDateTime(
                                                    slip.p_loading_end
                                                )}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Operator Start
                                            </span>

                                            <strong>
                                                {formatDateTime(
                                                    slip.p_load_unload_oper_Start
                                                )}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                TCODE
                                            </span>

                                            <strong>
                                                {tcodes || "-"}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Required Qty
                                            </span>

                                            <strong>
                                                {items
                                                    .reduce(
                                                        (
                                                            total,
                                                            item
                                                        ) =>
                                                            total +
                                                            numberValue(
                                                                item.QTY_REQUIRED
                                                            ),
                                                        0
                                                    )
                                                    .toFixed(2)}
                                            </strong>
                                        </div>

                                    </div>


                                    {/* ==================================
                                      REMARK
                                      ================================== */}

                                    {slip.P_LoadUnloadRemark && (

                                        <div className="mlt-remark">

                                            <span>
                                                Remark:
                                            </span>

                                            <strong>
                                                {
                                                    slip.P_LoadUnloadRemark
                                                }
                                            </strong>

                                        </div>

                                    )}

                                </div>
                            );
                        }
                    )}
                </>
            )}
        </>
    )
}

export default MyLoadedTrucks;